import { dayName, formatMonthYear, formatClock } from '@bookingdei/i18n/format-date';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { monthCells, isoDate as iso } from './month-cells';
import { useDateLabel } from './use-date-label';

export type WhenChip = 'any' | 'morning' | 'afternoon' | 'evening' | 'custom';
export const WHEN_CHIPS: WhenChip[] = ['any', 'morning', 'afternoon', 'evening', 'custom'];
const CHIP_KEYS = {
  any: 'dc50.when.any',
  morning: 'dc50.when.morning',
  afternoon: 'dc50.when.afternoon',
  evening: 'dc50.when.evening',
  custom: 'dc50.when.custom',
} as const;
// Custom-window hours offered in the From / To selects (design `hourOpts`).
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);
// Calendar header, Monday first (Date#getDay numbering).
const WEEK = [1, 2, 3, 4, 5, 6, 0];

export interface WhenValue {
  date: string; // ISO yyyy-mm-dd, '' for any day
  chip: WhenChip;
  from: number;
  to: number;
}

// Hero panel 3 (design DC:389-443): Today / Tomorrow shortcuts, a month calendar
// (past days disabled, today outlined), time-of-day chips with a custom From / To
// window, and Apply.
export function HeroWhenPanel({
  value,
  onChange,
  month,
  onMonth,
  now,
  onApply,
}: {
  value: WhenValue;
  onChange: (next: WhenValue) => void;
  month: { y: number; m: number };
  onMonth: (next: { y: number; m: number }) => void;
  now: Date;
  onApply: () => void;
}) {
  const { t, lang } = useLanguage();
  const dateLabel = useDateLabel();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const atStart = month.y === today.getFullYear() && month.m === today.getMonth();
  const cells = monthCells(month.y, month.m, now);
  const set = (patch: Partial<WhenValue>) => onChange({ ...value, ...patch });
  const quick = (d: Date, label: string) => {
    const on = value.date === iso(d);
    return (
      <button
        type="button"
        className={on ? 'hm-when__quick is-on' : 'hm-when__quick'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          set({ date: iso(d) });
          onMonth({ y: today.getFullYear(), m: today.getMonth() });
        }}
      >
        <span className="hm-when__quick-title">{label}</span>
        <span className="hm-when__quick-date">{dateLabel(d)}</span>
      </button>
    );
  };

  return (
    <div className="hm-panel hm-panel--when">
      <div className="hm-when__top">
        <div className="hm-when__quicks">
          {quick(today, t('dc50.when.today'))}
          {quick(tomorrow, t('dc50.when.tomorrow'))}
        </div>
        <div className="hm-when__cal">
          <div className="hm-when__calhead">
            <button
              type="button"
              className={atStart ? 'hm-when__nav is-off' : 'hm-when__nav'}
              aria-label={t('dc50.when.prev_month')}
              disabled={atStart}
              onClick={() => onMonth(month.m === 0 ? { y: month.y - 1, m: 11 } : { y: month.y, m: month.m - 1 })}
            >
              ‹
            </button>
            <span className="hm-when__title">{formatMonthYear(lang, new Date(month.y, month.m, 1))}</span>
            <button
              type="button"
              className="hm-when__nav"
              aria-label={t('dc50.when.next_month')}
              onClick={() => onMonth(month.m === 11 ? { y: month.y + 1, m: 0 } : { y: month.y, m: month.m + 1 })}
            >
              ›
            </button>
          </div>
          <div className="hm-when__week" aria-hidden="true">
            {WEEK.map((d) => (
              <span key={d}>{dayName(lang, d)}</span>
            ))}
          </div>
          <div className="hm-when__grid">
            {cells.map((c, i) =>
              c.iso ? (
                <button
                  key={c.iso}
                  type="button"
                  disabled={c.past}
                  aria-pressed={value.date === c.iso}
                  className={[
                    'hm-when__day',
                    c.past ? 'is-past' : '',
                    value.date === c.iso ? 'is-on' : '',
                    c.today && value.date !== c.iso ? 'is-today' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => set({ date: c.iso! })}
                >
                  {localiseDigits(lang, c.day)}
                </button>
              ) : (
                <span key={`b${i}`} className="hm-when__blank" />
              ),
            )}
          </div>
        </div>
      </div>
      <div className="hm-when__time">
        <div className="hm-panel__head hm-panel__head--flush">{t('dc50.when.select_time')}</div>
        <div className="hm-when__chips">
          {WHEN_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={value.chip === c}
              className={value.chip === c ? 'hm-when__chip is-on' : 'hm-when__chip'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => set({ chip: c })}
            >
              {t(CHIP_KEYS[c])}
            </button>
          ))}
        </div>
        {value.chip === 'custom' ? (
          <div className="hm-when__custom">
            <span className="hm-when__lbl">{t('dc50.when.from')}</span>
            <select
              className="hm-when__select"
              value={value.from}
              aria-label={t('dc50.when.from')}
              onChange={(e) => set({ from: Number(e.target.value) })}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {formatClock(lang, h, 0)}
                </option>
              ))}
            </select>
            <span className="hm-when__lbl">{t('dc50.when.to')}</span>
            <select
              className="hm-when__select"
              value={value.to}
              aria-label={t('dc50.when.to')}
              onChange={(e) => set({ to: Number(e.target.value) })}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {formatClock(lang, h, 0)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="hm-when__foot">
          <button type="button" className="hm-when__apply" onClick={onApply}>
            {t('dc50.when.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
